import { throwAppError } from '@bangle.io/base-utils';
import { WORKSPACE_STORAGE_TYPE } from '@bangle.io/constants';
import { useCoreServices } from '@bangle.io/context';
import { pickDirectory, supportsNativeFs } from '@bangle.io/native-fs';
import {
  createCloudWorkspace,
  getSession,
  isSupabaseConfigured,
} from '@bangle.io/supabase';
import { CreateWorkspaceDialog as UICreateWorkspaceDialog } from '@bangle.io/ui-components';
import { WsPath } from '@bangle.io/ws-path';
import { useAtom } from 'jotai';
import React from 'react';
import { nativeFsErrorParse } from '../common';
import { seedGettingStartedNote } from '../getting-started/seed-getting-started-note';

/** A dialog component for creating a new workspace, allowing selection of storage type. */
export function CreateWorkspaceDialog() {
  const coreServices = useCoreServices();
  const [openWsDialog, setOpenWsDialog] = useAtom(
    coreServices.workbenchState.$openWsDialog,
  );

  const storageTypes = [
    ...(isSupabaseConfigured()
      ? [
          {
            type: WORKSPACE_STORAGE_TYPE.Supabase,
            title: t.app.dialogs.createWorkspace.cloudTitle,
            description: t.app.dialogs.createWorkspace.cloudDescription,
            defaultSelected: true as const,
          },
        ]
      : []),
    {
      type: WORKSPACE_STORAGE_TYPE.Browser,
      title: t.app.dialogs.createWorkspace.browserTitle,
      description: t.app.dialogs.createWorkspace.browserDescription,
    },
    {
      type: WORKSPACE_STORAGE_TYPE.NativeFS,
      title: t.app.dialogs.createWorkspace.nativeFsTitle,
      description: t.app.dialogs.createWorkspace.nativeFsDescription,
      disabled: !supportsNativeFs(),
    },
  ];

  const finishCreate = async (wsName: string) => {
    setOpenWsDialog(false);
    try {
      const guideWsPath = await seedGettingStartedNote(
        coreServices.fileSystem,
        wsName,
      );
      coreServices.navigation.goWsPath(guideWsPath);
    } catch {
      coreServices.navigation.goWorkspace(wsName);
    }
  };

  return (
    <UICreateWorkspaceDialog
      open={openWsDialog}
      onOpenChange={setOpenWsDialog}
      validateWorkspace={({ name: wsName }) => {
        const result = WsPath.safeFromParts(wsName, '');
        if (result.ok) {
          return { isValid: true };
        }

        return {
          isValid: false,
          message:
            result.validationError?.reason ||
            t.app.dialogs.createWorkspace.invalidName,
        };
      }}
      onDone={async ({ name: wsName, type, dirHandle }) => {
        if (type === WORKSPACE_STORAGE_TYPE.NativeFS) {
          if (!dirHandle) {
            throwAppError(
              'error::workspace:invalid-metadata',
              `Directory handle for ${wsName} is invalid `,
              {
                wsName: wsName,
              },
            );
          }

          await coreServices.workspaceOps.createWorkspaceInfo({
            name: wsName,
            type,
            metadata: {
              rootDirHandle: dirHandle,
            },
          });
          await finishCreate(wsName);
          return;
        }

        if (type === WORKSPACE_STORAGE_TYPE.Browser) {
          await coreServices.workspaceOps.createWorkspaceInfo({
            metadata: {},
            name: wsName,
            type: WORKSPACE_STORAGE_TYPE.Browser,
          });
          await finishCreate(wsName);
          return;
        }

        if (type === WORKSPACE_STORAGE_TYPE.Supabase) {
          const session = await getSession();
          if (!session) {
            window.location.assign(
              `/login?next=${encodeURIComponent(window.location.href)}`,
            );
            return;
          }
          const remote = await createCloudWorkspace(wsName);
          await coreServices.workspaceOps.createWorkspaceInfo({
            name: wsName,
            type: WORKSPACE_STORAGE_TYPE.Supabase,
            metadata: {
              workspaceId: remote.id,
            },
          });
          await finishCreate(wsName);
          return;
        }

        throwAppError(
          'error::workspace:unknown-ws-type',
          'Unknown workspace type',
          {
            wsName: wsName,
            type,
          },
        );
      }}
      storageTypes={storageTypes}
      onDirectoryPick={async () => {
        try {
          const dirHandle = await pickDirectory({
            id: 'sariz-notes-workspace',
          });
          return { type: 'success', dirHandle };
        } catch (error) {
          if (!(error instanceof Error)) {
            throw error;
          }

          return nativeFsErrorParse(error);
        }
      }}
    />
  );
}
